import { CheckCheck } from 'lucide-react';
import { useId } from 'react';
import { toAppError } from '../../api/errors';
import { useMarkSeen } from '../../data/queries';
import { useT } from '../../l10n/i18n';
import { errorMessage } from '../core/presentation';
import { EmployeeAvatar } from '../core/widgets/EmployeeAvatar';
import { Skeleton } from '../core/widgets/common';
import { useToast } from '../core/widgets/toasts';
import { WorkEntries, type WorkEntriesProps } from './WorkEntries';
import {
  failedCount,
  isUrgent,
  reportsToRead,
  toDecideCount,
  toPublishCount,
  type EmployeeWork,
} from './workGroups';

/** One employee's waiting work: who, what needs the person, then the work line by line, with many of a
 * kind folded into one line. Ported from the mobile app's EmployeeWorkBlock. */
export function EmployeeWorkBlock({
  work,
  showWorkspace,
  ...lines
}: { work: EmployeeWork; /** Off when the list is already filtered to one workspace. */ showWorkspace: boolean } & Omit<
  WorkEntriesProps,
  'entries'
>) {
  const t = useT();
  const headingId = useId();
  const e = work.employee;
  const toDecide = toDecideCount(work.items);
  const toPublish = toPublishCount(work.items);
  const reports = reportsToRead(work.items);
  const failed = failedCount(work.items);
  const urgent = isUrgent(work);
  const gist = [
    toDecide > 0 && t('gistDecide', { count: toDecide }),
    toPublish > 0 && t('gistPublish', { count: toPublish }),
    reports.length > 0 && t('gistRead', { count: reports.length }),
    failed > 0 && t('gistFailed', { count: failed }),
  ].filter(Boolean);

  return (
    <article className="card work-block" aria-labelledby={headingId}>
      <header className="work-block-head">
        <EmployeeAvatar avatar={e.avatar} color={e.color} size={40} />
        <div className="work-block-who">
          <h2 id={headingId} className="title-medium">
            {e.name}
          </h2>
          <p className="body-small ellipsis">{showWorkspace ? `${work.workspace.name} · ${e.title}` : e.title}</p>
        </div>
        <span className={urgent ? 'work-block-count urgent' : 'work-block-count'}>{work.items.length}</span>
      </header>
      {/* What the work asks of the person; a single item says it on its own line. */}
      {work.items.length > 1 && gist.length > 0 && <p className="body-small work-block-gist">{gist.join(' · ')}</p>}
      <WorkEntries entries={work.entries} {...lines} />
      {reports.length > 1 && <SeenAll work={work} />}
    </article>
  );
}

/** Marks every report in the block seen; offered from two reports up. */
function SeenAll({ work }: { work: EmployeeWork }) {
  const t = useT();
  const toast = useToast();
  const markSeen = useMarkSeen();
  const reports = reportsToRead(work.items);
  const run = async () => {
    try {
      const count = await markSeen.mutateAsync(reports);
      toast(t('decidedSeenAll', { count }));
    } catch (e) {
      toast(errorMessage(t, toAppError(e)));
    }
  };
  return (
    <div className="work-block-foot">
      <button type="button" className="btn btn-text btn-small" disabled={markSeen.isPending} onClick={() => void run()}>
        <CheckCheck size={18} aria-hidden />
        {t('actionSeenAll', { count: reports.length })}
      </button>
    </div>
  );
}

/** The shape of a block while the inbox loads. */
export function WorkBlockSkeleton() {
  return (
    <div className="card work-block-skeleton" aria-hidden>
      <Skeleton width={44} height={44} radius={15} />
      <div className="work-block-skeleton-lines">
        <Skeleton width={120} height={11} />
        <Skeleton height={16} />
        <Skeleton width={200} height={12} />
        <div style={{ display: 'flex', gap: 6, marginTop: 4 }}>
          <Skeleton width={70} height={20} radius={99} />
          <Skeleton width={40} height={20} radius={99} />
        </div>
      </div>
    </div>
  );
}
